import instance from "./instance";
import { ChatSessionDto } from "./types/SuperChatLogTypes";
import { ENV } from "@/constants/env";

const PREFIX_PATH = `${ENV.PRE_FIX}/${ENV.ADMIN_PATH}`;

export const superChatLogApi = {
  /**
   * 채팅 세션 목록 조회
   */
  async getSessions(params?: {
    page?: number;
    limit?: number;
    sessionType?: string;
    sessionUserType?: string;
    status?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{
    list: ChatSessionDto[];
    count: number;
    page: number;
    limit: number;
  }> {
    const apiUrl = `${PREFIX_PATH}/super-chat-log/sessions`;
    const response = await instance.get(apiUrl, { params });

    return response.data;
  },

  /**
   * 세션 상세 조회
   */
  async getSessionDetail(sessionId: string): Promise<ChatSessionDto> {
    const apiUrl = `${PREFIX_PATH}/super-chat-log/sessions/${sessionId}`;
    const response = await instance.get(apiUrl);
    return response.data.data || response.data;
  },

  /**
   * 채팅 사용자 목록 조회
   */
  async getUsers(params?: {
    page?: number;
    limit?: number;
    userType?: string;
    businessScale?: string;
    status?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{
    list: Array<{
      userId: number;
      userName: string | null;
      userEmail: string | null;
      userPhone: string | null;
      userCompany: string | null;
      userType: string;
      status: string;
      regDttm: Date;
    }>;
    count: number;
    page: number;
    limit: number;
  }> {
    const apiUrl = `${PREFIX_PATH}/super-chat-log/users`;
    const response = await instance.get(apiUrl, { params });
    return response.data;
  },

  /**
   * 사용자 상세 조회
   */
  async getUserDetail(userId: string): Promise<{
    userId: number;
    userName: string | null;
    userEmail: string | null;
    userPhone: string | null;
    userCompany: string | null;
    userType: string;
    status: string;
    regDttm: Date;
  }> {
    const apiUrl = `${PREFIX_PATH}/super-chat-log/users/${userId}`;
    const response = await instance.get(apiUrl);
    return response.data.data;
  },

  /**
   * 채팅 통계 조회
   */
  async getStats(params?: { startDate?: string; endDate?: string }): Promise<{
    totalSessions: number;
    activeSessions: number;
    totalMessages: number;
    totalUsers: number;
    averageRating: number | null;
  }> {
    const apiUrl = `${PREFIX_PATH}/super-chat-log/stats`;
    const response = await instance.get(apiUrl, { params });
    return response.data.data;
  },

  /**
   * 대시보드 통계 조회
   */
  async getDashboardStats(): Promise<{
    totalSessions: number;
    activeSessions: number;
    totalMessages: number;
    totalUsers: number;
    averageRating: number | null;
  }> {
    const apiUrl = `${PREFIX_PATH}/super-chat-log/dashboard`;
    const response = await instance.get(apiUrl);
    return response.data.data;
  },

  /**
   * 세션 타입 목록 조회
   */
  async getSessionTypes(): Promise<string[]> {
    const apiUrl = `${PREFIX_PATH}/super-chat-log/session-types`;
    const response = await instance.get(apiUrl);
    return response.data.data;
  },

  /**
   * 사용자 타입 목록 조회
   */
  async getUserTypes(): Promise<string[]> {
    const apiUrl = `${PREFIX_PATH}/super-chat-log/user-types`;
    const response = await instance.get(apiUrl);
    return response.data.data;
  },

  /**
   * 세션별 메시지 조회
   */
  async getSessionMessages(
    sessionId: string,
    params?: {
      page?: number;
      limit?: number;
    }
  ): Promise<ChatSessionDto> {
    const apiUrl = `${PREFIX_PATH}/super-chat-log/sessions/${sessionId}`;
    const response = await instance.get(apiUrl, { params });

    // 백엔드 응답 구조에 따라 적절히 반환
    return response.data.data || response.data;
  },

  /**
   * 메시지 목록 조회
   */
  async getMessages(params?: {
    page?: number;
    limit?: number;
    sessionId?: string;
    senderType?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{
    list: Array<{
      messageId: number;
      sessionId: number;
      senderType: string;
      senderName: string | null;
      messageContent: string;
      messageType: string;
      sentAt: Date;
      isDeleted: boolean;
    }>;
    count: number;
    page: number;
    limit: number;
  }> {
    const apiUrl = `${PREFIX_PATH}/super-chat-log/messages`;
    const response = await instance.get(apiUrl, { params });
    return response.data;
  },

  /**
   * 메시지 삭제
   */
  async deleteMessage(messageId: number): Promise<void> {
    const apiUrl = `${PREFIX_PATH}/super-chat-log/messages/${messageId}`;
    await instance.delete(apiUrl);
  },

  /**
   * 메시지 검색
   */
  async searchMessages(params: {
    search: string;
    sessionId?: string;
    senderType?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{
    list: Array<{
      messageId: number;
      sessionId: number;
      senderType: string;
      senderName: string | null;
      messageContent: string;
      messageType: string;
      sentAt: Date;
      isDeleted: boolean;
    }>;
  }> {
    const apiUrl = `${PREFIX_PATH}/super-chat-log/messages/search`;
    const response = await instance.get(apiUrl, { params });
    return response.data;
  },

  /**
   * 세션 삭제
   */
  async deleteSession(sessionId: string): Promise<void> {
    const apiUrl = `${PREFIX_PATH}/super-chat-log/sessions/${sessionId}`;
    await instance.delete(apiUrl);
  },

  /**
   * 여러 세션 삭제
   */
  async deleteSessions(sessionIds: string[]): Promise<void> {
    const apiUrl = `${PREFIX_PATH}/super-chat-log/sessions/bulk-delete`;
    await instance.delete(apiUrl, { data: { sessionIds } });
  },

  /**
   * 통계 조회
   */
  async getStatistics(params?: {
    startDate?: string;
    endDate?: string;
    sessionType?: string;
    userType?: string;
  }): Promise<{
    overview: {
      totalSessions: number;
      totalMessages: number;
      activeSessions: number;
      avgMessagesPerSession: number;
      avgResponseTime: number;
    };
    sessionStatus: Array<{ status: string; count: number }>;
    categories: Array<{ category: string; count: number }>;
    dailyStats: Array<{ date: string; sessions: number; messages: number }>;
    messageTypes: Array<{ type: string; count: number }>;
  }> {
    const apiUrl = `${PREFIX_PATH}/super-chat-log/statistics`;
    const response = await instance.get(apiUrl, { params });
    return response.data.data;
  },

  /**
   * Excel 내보내기
   */
  async exportToExcel(params?: {
    startDate?: string;
    endDate?: string;
    sessionType?: string;
    userType?: string;
    format?: "sessions" | "messages";
  }): Promise<Blob> {
    const apiUrl = `${PREFIX_PATH}/super-chat-log/export/excel`;
    const response = await instance.get(apiUrl, {
      params,
      responseType: "blob",
    });
    return response.data;
  },

  /**
   * CSV 내보내기
   */
  async exportToCsv(params?: {
    startDate?: string;
    endDate?: string;
    sessionType?: string;
    userType?: string;
    format?: "sessions" | "messages";
  }): Promise<Blob> {
    const apiUrl = `${PREFIX_PATH}/super-chat-log/export/csv`;
    const response = await instance.get(apiUrl, {
      params,
      responseType: "blob",
    });
    return response.data;
  },
};
